/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { Icon, TimeDropdownSelect } from "../../ui";
import { rosterScheduleKey } from "../../employeeMatch";
import { formatHhmm, isValidShiftRange, serializeShiftRange, endValueForPicker } from "../../shiftTimeUtils";

import { SPECIAL_STATUSES, COVERAGE_HEATMAP_TOOLTIP_BG } from "./scheduleHelpers";

// ─── STAFF LOAN UI ───────────────────────────────────────────────────────────
export function normalizeLoanEmployeeCode(code) {
  const s = String(code || "").trim().toUpperCase();
  if (!s) return "";
  if (/^\d+$/.test(s)) return String(parseInt(s, 10));
  return s;
}

export function loanDateISO(loan) {
  return String(loan?.loan_date || "").slice(0, 10);
}

/** Host-store coverage row: show shift times when the loan is confirmed (or legacy loan_confirmed shift_type). */
export function loanShowsShiftAtHost(loan) {
  return String(loan?.status || "").toLowerCase() === "confirmed";
}

/** Match a dp_staff_loans row to a home-store OTHER_STORE cell. */
export function findStaffLoanForCell(loans, { employeeCode, employeeName, dateISO, store }) {
  const codeNorm = normalizeLoanEmployeeCode(employeeCode);
  const nameNorm = String(employeeName || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

  return (loans || []).find((l) => {
    if (l.status === "cancelled") return false;
    if (loanDateISO(l) !== dateISO) return false;
    if (store && l.home_store !== store && l.host_store !== store) return false;

    const loanCode = normalizeLoanEmployeeCode(l.employee_code);
    if (codeNorm && loanCode && loanCode === codeNorm) return true;

    const loanName = String(l.employee_name || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, " ");
    if (nameNorm && loanName && loanName === nameNorm) return true;

    return false;
  });
}

export function mergeStaffLoanLists(...lists) {
  const out = [];
  const seen = new Set();
  for (const list of lists) {
    for (const l of list || []) {
      if (!l?.id || seen.has(l.id)) continue;
      seen.add(l.id);
      out.push(l);
    }
  }
  return out;
}

export function openingHoursDefaultsForDayKey(openingHours, dayKeyLabel) {
  const dayName = String(dayKeyLabel || "").split(" ")[0];
  const oh = openingHours?.[dayName];
  if (!oh || oh === "CLOSED" || !String(oh).includes("-")) {
    return { shift_start: "09:00", shift_end: "17:00" };
  }
  const [a, b] = String(oh).split("-").map((s) => s.trim());
  return {
    shift_start: (a || "09:00").slice(0, 5),
    shift_end: (b || "17:00").slice(0, 5),
  };
}

export function LoanRequestModal({ isOpen, onClose, employee, homeStore, hostStore, dateLabel, lang, onSubmit, submitting }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setReason("");
  }, [isOpen, employee?.id, dateLabel]);

  if (!isOpen || !employee) return null;

  const t = (en, gr) => (lang === "gr" ? gr : en);
  const name = employee.name || employee.employee_code || "—";
  const home = homeStore || t("another store", "άλλο κατάστημα");

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="modal-box" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-title">
          {t(`Request loan — ${name}`, `Αίτημα δανεισμού — ${name}`)}
        </div>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
          {hostStore && dateLabel
            ? t(
                `${name} will be loaned to ${hostStore} on ${dateLabel}`,
                `Ο/Η ${name} θα δανειστεί στο ${hostStore} ${dateLabel}`
              )
            : t(
                `${name} is based at ${home}. Requesting a loan will notify HR and Ops for approval.`,
                `Ο/Η ${name} ανήκει στο ${home}. Το αίτημα δανεισμού θα σταλεί στο HR και Ops για έγκριση.`
              )}
        </p>
        <div className="form-group">
          <label className="form-label">{t("Reason", "Αιτία")} *</label>
          <textarea
            className="form-textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder={t("Why is this loan needed?", "Γιατί χρειάζεται ο δανεισμός;")}
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button
            type="button"
            className="btn-base btn-outline"
            style={{ flex: 1, justifyContent: "center" }}
            disabled={submitting}
            onClick={onClose}
          >
            {t("Cancel", "Ακύρωση")}
          </button>
          <button
            type="button"
            className="btn-base btn-green"
            style={{ flex: 1, justifyContent: "center" }}
            disabled={submitting || !reason.trim()}
            onClick={() => onSubmit(reason.trim())}
          >
            {submitting ? t("Sending…", "Αποστολή…") : t("Send loan request", "Αποστολή αιτήματος")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function LoanModal({
  isOpen,
  onClose,
  employee,
  date,
  dateLabel,
  currentStore,
  allStores,
  openingHours,
  lang,
  onSubmit,
  submitting,
}) {
  const hostOptions = useMemo(
    () => (allStores || []).filter((s) => s && s !== currentStore && s !== "All Stores"),
    [allStores, currentStore]
  );
  const [hostStore, setHostStore] = useState("");
  const [shiftStart, setShiftStart] = useState("09:00");
  const [shiftEnd, setShiftEnd] = useState("17:00");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const defs = openingHoursDefaultsForDayKey(openingHours, dateLabel);
    setShiftStart(defs.shift_start);
    setShiftEnd(defs.shift_end);
    setReason("");
    setHostStore(hostOptions[0] || "");
  }, [isOpen, dateLabel, openingHours, hostOptions]);

  if (!isOpen || !employee) return null;

  const t = (en, gr) => (lang === "gr" ? gr : en);
  const shiftRangeInvalid =
    Boolean(shiftStart && shiftEnd) && !isValidShiftRange(shiftStart, shiftEnd);

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
>
      <div className="modal-box" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-title">{t("Staff loan", "Δανεισμός προσωπικού")}</div>
        {hostStore && dateLabel ? (
          <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
            {t(
              `${employee.name} will be loaned to ${hostStore} on ${dateLabel}`,
              `Ο/Η ${employee.name} θα δανειστεί στο ${hostStore} ${dateLabel}`
            )}
          </p>
        ) : null}

        <div className="form-group">
          <label className="form-label">{t("Employee", "Υπάλληλος")}</label>
          <div
            className="form-input"
            style={{ background: "var(--surface2)", fontWeight: 700, cursor: "default" }}
          >
            {employee.name}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t("Date", "Ημερομηνία")}</label>
          <div
            className="form-input"
            style={{ background: "var(--surface2)", fontWeight: 600, cursor: "default" }}
          >
            {dateLabel || date}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t("Host store", "Κατάστημα φιλοξενίας")}</label>
          <select
            className="form-select"
            value={hostStore}
            onChange={(e) => setHostStore(e.target.value)}
            disabled={submitting || hostOptions.length === 0}
          >
            {hostOptions.length === 0 ? (
              <option value="">{t("No other stores", "Δεν υπάρχουν άλλα καταστήματα")}</option>
            ) : (
              hostOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))
            )}
          </select>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <TimeDropdownSelect
              label={t("Shift start", "Έναρξη")}
              labelClassName="form-label"
              value={shiftStart}
              onChange={setShiftStart}
              disabled={submitting}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <TimeDropdownSelect
              label={t("Shift end", "Λήξη")}
              labelClassName="form-label"
              value={shiftEnd}
              onChange={setShiftEnd}
              disabled={submitting}
              allowNextDayEnds
              lang={lang}
            />
          </div>
        </div>
        {shiftRangeInvalid ? (
          <div
            style={{
              marginTop: -4,
              marginBottom: 10,
              fontSize: 12,
              fontWeight: 700,
              color: "#B91C1C",
            }}
          >
            {t("End time must be after start time.", "Η ώρα λήξης πρέπει να είναι μετά την έναρξη.")}
          </div>
        ) : null}

        <div className="form-group">
          <label className="form-label">{t("Reason (optional)", "Αιτιολογία (προαιρετικό)")}</label>
          <textarea
            className="form-textarea"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder={t("Optional note for HR…", "Προαιρετική σημείωση για HR…")}
          />
        </div>

        <div style={{ display: "flex", gap: 9, marginTop: 8 }}>
          <button
            type="button"
            className="btn-base btn-green"
            style={{ flex: 1, justifyContent: "center" }}
            disabled={submitting || !hostStore || shiftRangeInvalid}
            onClick={() => {
              if (shiftRangeInvalid) return;
              onSubmit({
                host_store: hostStore,
                shift_start: shiftStart,
                shift_end: shiftEnd,
                reason: reason.trim() || null,
              });
            }}
          >
            {submitting ? t("Saving…", "Αποθήκευση…") : t("Submit loan", "Υποβολή δανεισμού")}
          </button>
          <button
            type="button"
            className="btn-base btn-outline"
            onClick={onClose}
            disabled={submitting}
          >
            {t("Cancel", "Ακύρωση")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function formatLoanCellStoreLabel({ loan, currentStore, fallbackHomeStore, lang }) {
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const home = String(loan?.home_store || fallbackHomeStore || "").trim();
  const host = String(loan?.host_store || "").trim();
  const viewing = String(currentStore || "").trim();

  if (!home && !host) return "";

  if (viewing && host && viewing === host && home) {
    return t(`from ${home}`, `από ${home}`);
  }
  if (viewing && home && viewing === home && host) {
    return t(`to ${host}`, `προς ${host}`);
  }
  if (viewing && host && viewing === host && fallbackHomeStore) {
    return t(`from ${fallbackHomeStore}`, `από ${fallbackHomeStore}`);
  }
  if (host) {
    return t(`to ${host}`, `προς ${host}`);
  }
  return home;
}

export function OtherStoreLoanCell({
  employeeCode,
  employeeName,
  dateISO,
  store,
  unifiedLocationId = null,
  lang,
  user,
  weekLoans,
  shiftType = "",
  homeStoreLabel = "",
  onCreateLoan,
  onClearShift,
}) {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  const bumpLayout = useCallback(() => setLayoutKey((k) => k + 1), []);

  const canCancel =
    user?.role === "manager" || user?.role === "admin" || user?.role === "brand_manager" || user?.role === "store_manager";

  const matchCtx = useMemo(
    () => ({ employeeCode, employeeName, dateISO, store }),
    [employeeCode, employeeName, dateISO, store]
  );

  const cellLoan = useMemo(
    () => findStaffLoanForCell(weekLoans, matchCtx),
    [weekLoans, matchCtx]
  );
  const displayLoan = loan || cellLoan;
  const isConfirmed =
    shiftType === "loan_confirmed" || (displayLoan ? loanShowsShiftAtHost(displayLoan) : false);
  const loanStoreLabel = formatLoanCellStoreLabel({
    loan: displayLoan,
    currentStore: store,
    fallbackHomeStore: homeStoreLabel,
    lang,
  });
  const loanPopupStoreLine =
    displayLoan?.home_store && displayLoan?.host_store
      ? `${displayLoan.home_store} → ${displayLoan.host_store}`
      : displayLoan?.host_store || displayLoan?.home_store || "";
  const loanVisual = isConfirmed
    ? {
        background: "#CCFBF1",
        border: "1.5px solid #0D9488",
        color: "#0F766E",
        storeColor: "#115E59",
        label: lang === "gr" ? "Δανεισμός" : "Loan",
      }
    : {
        background: "#FEF3C7",
        border: "1.5px dashed #F59E0B",
        color: "#B45309",
        storeColor: "#92400E",
        label: lang === "gr" ? "Δανεισμός;" : "Loan?",
      };

  useEffect(() => {
    if (!open || unifiedLocationId == null) return;
    let cancelled = false;

    const cached = findStaffLoanForCell(weekLoans, matchCtx);
    if (cached) {
      setLoan(cached);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    fetch(
      `${API_BASE}/staff/loans?unified_location_id=${encodeURIComponent(unifiedLocationId)}&date=${encodeURIComponent(dateISO)}`,
      { headers: getAuthHeaders() }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (cancelled) return;
        const merged = mergeStaffLoanLists(weekLoans, rows);
        setLoan(findStaffLoanForCell(merged, matchCtx) || null);
      })
      .catch(() => {
        if (!cancelled) setLoan(findStaffLoanForCell(weekLoans, matchCtx) || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, store, dateISO, employeeCode, employeeName, weekLoans, matchCtx]);

  useEffect(() => {
    if (!open) return undefined;
    bumpLayout();
    const onLayout = () => bumpLayout();
    window.addEventListener("scroll", onLayout, true);
    window.addEventListener("resize", onLayout);
    return () => {
      window.removeEventListener("scroll", onLayout, true);
      window.removeEventListener("resize", onLayout);
    };
  }, [open, bumpLayout]);

  const popupLayout = useMemo(() => {
    if (!open || typeof window === "undefined") return null;
    const el = triggerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: rect.left + rect.width / 2,
    };
  }, [open, layoutKey]);

  const t = (en, gr) => (lang === "gr" ? gr : en);

  function handleClearShift() {
    if (cancelling) return;
    setCancelling(true);
    Promise.resolve(onClearShift?.(loan?.id))
      .then(() => setOpen(false))
      .catch(() => {})
      .finally(() => setCancelling(false));
  }

  const loanPopupPanel =
    open && popupLayout ? (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          zIndex: 12000,
          top: popupLayout.top,
          left: popupLayout.left,
          transform: "translateX(-50%)",
          minWidth: 200,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(13,43,29,0.18)",
          padding: 12,
          textAlign: "left",
        }}
      >
        {loading ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{t("Loading…", "Φόρτωση…")}</div>
        ) : loan ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-primary)", marginBottom: 6 }}>
              {loanPopupStoreLine}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 6 }}>
              {(loan.shift_start || "").slice(0, 5)}–{(loan.shift_end || "").slice(0, 5)}
            </div>
            <span
              style={{
                display: "inline-block",
                fontSize: 10,
                fontWeight: 800,
                padding: "2px 8px",
                borderRadius: 8,
                background:
                  loan.status === "confirmed"
                    ? "#DCFCE7"
                    : loan.status === "cancelled"
                      ? "#FEE2E2"
                      : "#FEF9C3",
                color:
                  loan.status === "confirmed"
                    ? "#166534"
                    : loan.status === "cancelled"
                      ? "#991B1B"
                      : "#854D0E",
              }}
            >
              {loan.status === "pending"
                ? t("pending (legacy)", "εκκρεμεί (παλιό)")
                : loan.status === "confirmed"
                  ? t("confirmed", "επιβεβαιωμένο")
                  : loan.status}
            </span>
            {canCancel && loan.status !== "cancelled" && (
              <button
                type="button"
                className="btn-base btn-danger-soft btn-small"
                style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
                disabled={cancelling}
                onClick={handleClearShift}
              >
                {t("Cancel loan", "Ακύρωση Δανεισμού")}
              </button>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45, marginBottom: 10 }}>
              {t(
                "No loan record — usually an imported schedule or a cell set before loans were enabled.",
                "Δεν βρέθηκε καταχώρηση — συνήθως εισαγόμενο πρόγραμμα ή κελί πριν τον δανεισμό."
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {onCreateLoan && (
                <button
                  type="button"
                  className="btn-base btn-green btn-small"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => {
                    setOpen(false);
                    onCreateLoan();
                  }}
                >
                  {t("Register loan", "Καταχώρηση δανεισμού")}
                </button>
              )}
              <button
                type="button"
                className="btn-base btn-outline btn-small"
                style={{ width: "100%", justifyContent: "center" }}
                disabled={cancelling}
                onClick={handleClearShift}
              >
                {cancelling
                  ? t("Clearing…", "Αφαίρεση…")
                  : t("Clear cell", "Καθαρισμός κελιού")}
              </button>
            </div>
          </>
        )}
      </div>
    ) : null;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 48 }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => {
            const next = !v;
            if (next) bumpLayout();
            return next;
          });
        }}
        style={{
          width: "100%",
          minHeight: 48,
          boxSizing: "border-box",
          background: loanVisual.background,
          border: loanVisual.border,
          color: loanVisual.color,
          fontWeight: 800,
          fontSize: 11,
          cursor: "pointer",
          borderRadius: 0,
          padding: "5px 4px",
        }}
      >
        <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, lineHeight: 1.15 }}>
          <span>{loanVisual.label}</span>
          {loanStoreLabel ? (
            <span style={{ fontSize: 9, fontWeight: 500, fontStyle: "italic", color: loanVisual.storeColor }}>
              {loanStoreLabel}
            </span>
          ) : null}
        </span>
      </button>
      {loanPopupPanel && typeof document !== "undefined"
        ? createPortal(loanPopupPanel, document.body)
        : null}
    </div>
  );
}

// ─── SHIFT PICKER COMPONENT ──────────────────────────────────────────────────
export function formatTime(t) {
  if (!t) return "";
  const parts = String(t).split(":");
  return parts[0].padStart(2, "0") + ":" + (parts[1] || "00").padStart(2, "0");
}

export function ShiftPicker({
  activeCell,
  activeEmp,
  cellVal,
  assignmentWarning,
  shiftOptions,
  adminScheduleOverride = false,
  adminShiftPresetUnion = [],
  lang,
  onSelect,
  onClear,
  onClose,
  onAddCustom: _onAddCustom,
  empShiftHours,
  canMarkRestRequested = false,
  restDayAlreadyRequested = false,
  markRestRequestedLoading = false,
  removeRestRequestedLoading = false,
  onMarkRestRequested,
  onRemoveRestRequested,
}) {
  const [showCustom, setShowCustom] = useState(false);
  const [showAllDurations, setShowAllDurations] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectionFeedback, setSelectionFeedback] = useState("");
  const [showMarkRestConfirm, setShowMarkRestConfirm] = useState(false);
  const selectionFeedbackTimeoutRef = useRef(null);
  const displayedShiftOptions =
    adminScheduleOverride && showAllDurations ? adminShiftPresetUnion : shiftOptions;

  function shiftRangeDisplay(val) {
    const m = String(val || "").trim().match(/^(\d{1,2}:\d{2}(?::\d{2})?)-(\d{1,2}:\d{2}(?::\d{2})?)$/);
    if (!m) return null;
    return `${formatTime(m[1])}–${formatTime(m[2])}`;
  }

  function handleSelectValue(val) {
    onSelect(val);
    const disp = shiftRangeDisplay(val) || val;
    const label = lang === "gr" ? `Ανατέθηκε: ${disp}` : `Assigned: ${disp}`;
    setSelectionFeedback(label);
    if (selectionFeedbackTimeoutRef.current) clearTimeout(selectionFeedbackTimeoutRef.current);
    selectionFeedbackTimeoutRef.current = setTimeout(() => setSelectionFeedback(""), 4000);
  }

  function applyCustomShift(start, end) {
    if (!start || !end) return;
    if (!isValidShiftRange(start, end)) return;
    handleSelectValue(serializeShiftRange(start, end));
  }

  const customRangeInvalid =
    Boolean(customStart && customEnd) && !isValidShiftRange(customStart, customEnd);

  useEffect(() => {
    return () => {
      if (selectionFeedbackTimeoutRef.current) clearTimeout(selectionFeedbackTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setShowCustom(false);
    setShowAllDurations(false);
    setShowMarkRestConfirm(false);
    const v = cellVal && String(cellVal).trim();
    const m = v && v.match(/^(\d{1,2}:\d{2}(?::\d{2})?)-(\d{1,2}:\d{2}(?::\d{2})?)$/);
    if (m) {
      const start = formatHhmm(m[1]);
      const end = formatHhmm(m[2]);
      setCustomStart(start);
      setCustomEnd(endValueForPicker(start, end));
    } else {
      setCustomStart("09:00");
      setCustomEnd("");
    }
  }, [activeCell?.empId, activeCell?.dk, cellVal]);

  useEffect(() => {
    if (restDayAlreadyRequested) setShowMarkRestConfirm(false);
  }, [restDayAlreadyRequested]);

  return (
    <div style={{ marginTop: 14, background: "var(--surface)", border: "1.5px solid var(--green)", borderRadius: 18, padding: 18, boxShadow: "0 4px 24px rgba(27,67,50,0.12)" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <span style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)" }}>{activeEmp?.name}</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-muted)" }}>· {activeCell.dk}</span>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 10, background: empShiftHours === 8 ? "#DCFCE7" : "#EDE9FE", color: empShiftHours === 8 ? "#15803D" : "#6D28D9", fontWeight: 800, padding: "3px 9px", borderRadius: 20 }}>{empShiftHours}HR</span>
          <span style={{ fontSize: 10, background: "#FEF9C3", color: "#854D0E", fontWeight: 800, padding: "3px 9px", borderRadius: 20 }}>{activeEmp?.workDays}D/W</span>
          <button onClick={onClose} style={{ border: "none", background: "var(--surface2)", borderRadius: 8, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="close" size={14} />
          </button>
        </div>
      </div>

      {assignmentWarning && (
        <div style={{ marginBottom: 12, background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA", borderRadius: 10, padding: "8px 10px", fontSize: 12, fontWeight: 700 }}>
          {assignmentWarning}
        </div>
      )}
      {selectionFeedback && !assignmentWarning && (
        <div style={{ marginBottom: 12, background: "#ECFDF5", color: "#166534", border: "1px solid #BBF7D0", borderRadius: 10, padding: "8px 10px", fontSize: 12, fontWeight: 700 }}>
          {selectionFeedback}
        </div>
      )}

      {/* Current value */}
      {cellVal && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", background: "var(--surface2)", borderRadius: 10 }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{lang === "gr" ? "Τρέχουσα:" : "Current:"}</span>
          <span style={{ fontWeight: 800, fontSize: 13, color: "var(--green)" }}>{shiftRangeDisplay(cellVal) || (cellVal === "OFF" ? (lang === "gr" ? "Ρεπό" : "Rest Day") : cellVal)}</span>
          <button onClick={onClear} className="btn-base btn-danger-soft btn-small" style={{ marginLeft: "auto", padding: "4px 10px" }}>{lang === "gr" ? "Καθαρισμός" : "Clear"}</button>
        </div>
      )}

      {canMarkRestRequested && cellVal === "OFF" && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            background: restDayAlreadyRequested ? "#FFFBEB" : "var(--surface2)",
            border: "1px solid var(--border)",
            borderRadius: 10,
          }}
        >
          {restDayAlreadyRequested ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", fontStyle: "italic" }}>
                {lang === "gr" ? "Αιτήθηκε από εργαζόμενο" : "Requested by employee"}
              </div>
              <button
                type="button"
                disabled={removeRestRequestedLoading}
                onClick={() => {
                  if (typeof onRemoveRestRequested === "function") onRemoveRestRequested();
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: removeRestRequestedLoading ? "wait" : "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  fontFamily: "DM Sans, sans-serif",
                  fontStyle: "italic",
                  textDecoration: "underline",
                  opacity: removeRestRequestedLoading ? 0.7 : 1,
                }}
              >
                {removeRestRequestedLoading
                  ? (lang === "gr" ? "Αφαίρεση…" : "Removing…")
                  : (lang === "gr" ? "Αφαίρεση" : "Remove")}
              </button>
            </div>
          ) : showMarkRestConfirm ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginBottom: 10 }}>
                {lang === "gr" ? "Καταχώρηση ως αίτηση εργαζόμενου;" : "Mark as employee-requested?"}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  disabled={markRestRequestedLoading}
                  onClick={async () => {
                    const ok =
                      typeof onMarkRestRequested === "function"
                        ? await onMarkRestRequested()
                        : false;
                    // Parent owns requested state via pendingLocalRestRequests;
                    // only dismiss confirm UI here on success.
                    if (ok) setShowMarkRestConfirm(false);
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: "none",
                    background: "var(--green)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: markRestRequestedLoading ? "wait" : "pointer",
                    opacity: markRestRequestedLoading ? 0.7 : 1,
                    fontFamily: "DM Sans, sans-serif",
                  }}
                >
                  {markRestRequestedLoading
                    ? (lang === "gr" ? "Αποθήκευση…" : "Saving…")
                    : (lang === "gr" ? "Ναι, καταχώρηση" : "Yes, mark as requested")}
                </button>
                <button
                  type="button"
                  disabled={markRestRequestedLoading}
                  onClick={() => setShowMarkRestConfirm(false)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: "1.5px solid var(--border)",
                    background: "transparent",
                    color: "var(--text-secondary)",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "DM Sans, sans-serif",
                  }}
                >
                  {lang === "gr" ? "Άκυρο" : "Cancel"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowMarkRestConfirm(true)}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                color: "var(--green)",
                fontFamily: "DM Sans, sans-serif",
                textDecoration: "underline",
              }}
            >
              {lang === "gr" ? "Καταχώρηση ως αίτηση εργαζόμενου;" : "Mark as employee-requested?"}
            </button>
          )}
        </div>
      )}

      {/* Shift presets */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
            {adminScheduleOverride && showAllDurations
              ? lang === "gr"
                ? "Όλες οι διάρκειες"
                : "All durations"
              : `${empShiftHours}hr ${lang === "gr" ? "Βάρδιες" : "Shifts"}`}
          </div>
          {adminScheduleOverride && (
            <button
              type="button"
              onClick={() => setShowAllDurations((v) => !v)}
              style={{
                border: "none",
                background: "transparent",
                padding: "2px 0",
                fontSize: 10,
                fontWeight: 600,
                color: "var(--text-muted)",
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
                textDecoration: showAllDurations ? "none" : "underline",
                textUnderlineOffset: 2,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {showAllDurations
                ? lang === "gr"
                  ? `Μόνο ${empShiftHours}ω`
                  : `${empShiftHours}hr only`
                : lang === "gr"
                  ? "Όλες οι διάρκειες"
                  : "Show all durations"}
            </button>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {displayedShiftOptions.map(sh => (
            <button key={sh} onClick={() => handleSelectValue(sh)} style={{ padding: "6px 12px", borderRadius: 10, border: "1.5px solid", borderColor: cellVal === sh ? "var(--green)" : "var(--border)", background: cellVal === sh ? "var(--green)" : "var(--surface2)", color: cellVal === sh ? "#fff" : "var(--text-primary)", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.12s" }}>{sh}</button>
          ))}

          {/* Inline custom shift toggle — small & discrete */}
          <button
            onClick={() => setShowCustom(v => !v)}
            style={{ padding: "6px 11px", borderRadius: 10, border: "1.5px dashed", borderColor: showCustom ? "var(--green)" : "var(--border)", background: showCustom ? "rgba(27,67,50,0.05)" : "transparent", color: showCustom ? "var(--green)" : "var(--text-muted)", fontWeight: 700, fontSize: 11, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.12s", display: "flex", alignItems: "center", gap: 4 }}
            title={lang === "gr" ? "Δημιουργία προσαρμοσμένης βάρδιας" : "Create custom shift"}
          >
            <span style={{ fontSize: 14 }}>+</span> {lang === "gr" ? "Νέα" : "Custom"}
          </button>
        </div>
        {showCustom && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
            <TimeDropdownSelect
              label={lang === "gr" ? "Έναρξη" : "Start"}
              value={customStart}
              onChange={setCustomStart}
            />
            <TimeDropdownSelect
              label={lang === "gr" ? "Λήξη" : "End"}
              value={customEnd}
              onChange={setCustomEnd}
              allowNextDayEnds
              lang={lang}
            />
            <button
              type="button"
              onClick={() => {
                if (customStart && customEnd) applyCustomShift(customStart, customEnd);
              }}
              disabled={customRangeInvalid}
              style={{
                marginTop: 16,
                padding: "6px 14px",
                borderRadius: 8,
                background: customRangeInvalid ? "var(--border)" : "var(--green)",
                color: customRangeInvalid ? "var(--text-muted)" : "#fff",
                border: "none",
                fontSize: 12,
                fontWeight: 700,
                cursor: customRangeInvalid ? "not-allowed" : "pointer",
                opacity: customRangeInvalid ? 0.6 : 1,
              }}
            >
              Apply
            </button>
          </div>
        )}
        {showCustom && customRangeInvalid ? (
          <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: "#B91C1C" }}>
            {lang === "gr" ? "Η ώρα λήξης πρέπει να είναι μετά την έναρξη." : "End time must be after start time."}
          </div>
        ) : null}
      </div>

      {/* Special statuses */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{lang === "gr" ? "Ειδικές Καταστάσεις" : "Special Statuses"}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SPECIAL_STATUSES.map(sp => (
            <button key={sp.value} onClick={() => handleSelectValue(sp.value)} style={{ padding: "6px 12px", borderRadius: 10, border: "2px solid", borderColor: cellVal === sp.value ? "rgba(0,0,0,0.3)" : "transparent", background: sp.color, color: sp.textColor, fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "DM Sans, sans-serif", boxShadow: cellVal === sp.value ? "inset 0 0 0 2px rgba(0,0,0,0.15)" : "none", transition: "all 0.12s" }}>{lang === "gr" ? sp.labelGr : sp.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── STAFFING HEATMAP COMPONENT ───────────────────────────────────────────────
export function StaffingHeatmap({ schedule, weekDates, dayNames, dayNamesGr, allUsers, store, openingHours, lang, parseShiftMins, fmtDate, demandIntensity, hourlyTargets, isMobile = false, mobileDay = 0, headerRightAction = null, onHeaderClick = null }) {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showAllMobileHours, setShowAllMobileHours] = useState(false);

  const INTENSITY = demandIntensity
    ? Object.fromEntries(
        Object.entries(demandIntensity).map(([h, v]) => [parseInt(h, 10), v]),
      )
    : {
        8: 2, 9: 3, 10: 5, 11: 12, 12: 30, 13: 60, 14: 92, 15: 100,
        16: 81, 17: 58, 18: 43, 19: 37, 20: 38, 21: 38, 22: 30, 23: 11,
      };

  const storeEmps = allUsers.filter(u => u.store === store);

  function openHour(di) {
    const oh = openingHours[dayNames[di]];
    if (!oh || oh === "CLOSED") return null;
    const parts = oh.split("-");
    if (parts.length !== 2) return null;
    const open = parseInt(parts[0].split(":")[0], 10);
    if (Number.isNaN(open)) return null;
    return open;
  }
  function closeHour(di) {
    const oh = openingHours[dayNames[di]];
    const open = openHour(di);
    if (open === null || !oh || oh === "CLOSED") return null;
    const parts = oh.split("-");
    if (parts.length !== 2) return null;
    const h = parseInt(parts[1]?.split(":")[0] || "", 10);
    if (Number.isNaN(h)) return null;
    return h < open ? h + 24 : h;
  }
  function isoDate(d) {
    const yr  = d.getFullYear();
    const mo  = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${day}`;
  }
  function countAtHour(di, hour) {
    const dk = dayNames[di] + " " + weekDates[di].toLocaleDateString("en-GB", { day:"2-digit", month:"short" });
    return storeEmps.filter(emp => {
      const shift = parseShiftMins(schedule[rosterScheduleKey(emp)]?.[dk] || "");
      if (!shift) return false;
      return shift.start <= hour * 60 && hour * 60 < shift.end;
    }).length;
  }

  function countGroupAtHour(di, hour, group) {
    const dk = dayNames[di] + " " + weekDates[di].toLocaleDateString("en-GB", { day:"2-digit", month:"short" });
    return storeEmps.filter(emp => {
      if (emp.group !== group) return false;
      const shift = parseShiftMins(schedule[rosterScheduleKey(emp)]?.[dk] || "");
      if (!shift) return false;
      return shift.start <= hour * 60 && hour * 60 < shift.end;
    }).length;
  }

  // All unique hours across the full week
  const heatmapHours = hourlyTargets
    ? Object.keys(hourlyTargets).map(Number)
    : Object.keys(INTENSITY).map(Number);
  const allHours = Array.from(new Set(
    weekDates.flatMap((_, di) => {
      const o = openHour(di), c = closeHour(di);
      if (o === null || c === null) return [];
      return heatmapHours.filter(h => h >= o && h < c);
    })
  )).sort((a,b) => a - b);

  // Cell background: blend demand intensity colour with staff count
  function cellColor(count, intensity) {
    if (intensity < 10) return { bg: "#F9FAFB", text: "#9CA3AF" };  // closed / quiet — neutral
    if (count === 0 && intensity >= 40) return { bg: "#FEE2E2", text: "#DC2626" }; // understaffed peak — red
    if (count === 0) return { bg: "#F3F4F6", text: "#9CA3AF" };     // no staff, low demand — grey
    // Staff present: colour intensity of green based on how well covered
    if (intensity >= 85) {
      // peak hours — need more staff, use orange/green gradient
      if (count >= 4) return { bg: "#D1FAE5", text: "#065F46" };
      if (count >= 3) return { bg: "#FEF3C7", text: "#92400E" };
      if (count >= 2) return { bg: "#FED7AA", text: "#9A3412" };
      return { bg: "#FECACA", text: "#991B1B" };
    }
    if (intensity >= 50) {
      if (count >= 3) return { bg: "#D1FAE5", text: "#065F46" };
      if (count >= 2) return { bg: "#D1FAE5", text: "#065F46" };
      return { bg: "#FEF9C3", text: "#854D0E" };
    }
    // moderate / quiet — green is fine with even 1-2 people
    return { bg: "#D1FAE5", text: "#065F46" };
  }

  function demandLabel(pct) {
    if (pct >= 85) return { dot: "#DC2626", label: lang === "gr" ? "Αιχμή" : "Peak" };
    if (pct >= 55) return { dot: "#EA580C", label: lang === "gr" ? "Πολυάσχολο" : "Busy" };
    if (pct >= 25) return { dot: "#CA8A04", label: lang === "gr" ? "Μέτριο" : "Moderate" };
    return           { dot: "#9CA3AF",  label: lang === "gr" ? "Ήσυχο" : "Quiet" };
  }

  const GROUP_ORDER = [
    { key: "supervisor", short: "S", name: "Supervisor" },
    { key: "service", short: "Sv", name: "Service" },
    { key: "kitchen", short: "K", name: "Kitchen" },
    { key: "cleaning", short: "C", name: "Cleaning" },
  ];

  function groupCellColor(scheduled, recommended, closed) {
    if (closed) return { bg: "#F9FAFB", text: "#E5E7EB" };
    if (scheduled === 0 && recommended > 0) return { bg: "#FEE2E2", text: "#DC2626" };
    if (scheduled >= recommended) return { bg: "#D1FAE5", text: "#065F46" };
    if (scheduled === recommended - 1) return { bg: "#FEF3C7", text: "#92400E" };
    return { bg: "#FEE2E2", text: "#DC2626" };
  }

  function tooltipDotColor(bg) {
    if (bg === "#D1FAE5") return "#22C55E";
    if (bg === "#FEF3C7") return "#F59E0B";
    if (bg === "#F9FAFB") return "#9CA3AF";
    return "#EF4444";
  }

  function mobileCoverage(hour, dayIndex) {
    const open = openHour(dayIndex);
    const close = closeHour(dayIndex);
    const closed = open === null || close === null || hour < open || hour >= close;
    if (closed) return { tone: "closed", count: 0 };
    if (hourlyTargets == null) {
      const count = countAtHour(dayIndex, hour);
      if (count === 0) return { tone: "red", count };
      if (count === 1) return { tone: "amber", count };
      return { tone: "green", count };
    }
    const dateStr = isoDate(weekDates[dayIndex]);
    const hourTargets = hourlyTargets?.[String(hour)]?.[dateStr] || {};
    const groupRows = GROUP_ORDER.map(({ key }) => {
      const recommended = Number(hourTargets[key] ?? 0);
      const scheduled = countGroupAtHour(dayIndex, hour, key);
      return { recommended, scheduled };
    });
    const anyZeroOpen = groupRows.some(r => r.recommended > 0 && r.scheduled === 0);
    const anyOneBelow = groupRows.some(r => r.recommended > 0 && r.recommended - r.scheduled === 1);
    const count = countAtHour(dayIndex, hour);
    if (anyZeroOpen) return { tone: "red", count };
    if (anyOneBelow) return { tone: "amber", count };
    return { tone: "green", count };
  }

  const hoveredInfo = hovered ? (() => {
    const { di, hour } = hovered;
    const open = openHour(di);
    const close = closeHour(di);
    const closed = open === null || close === null || hour < open || hour >= close;
    const header = `${dayNames[di]} ${fmtDate(weekDates[di])} · ${String(hour % 24).padStart(2,"0")}:00–${String((hour + 1) % 24).padStart(2,"0")}:00`;
    if (hourlyTargets == null) {
      const count = closed ? 0 : countAtHour(di, hour);
      return { mode: "fallback", header, count };
    }
    const dateStr = isoDate(weekDates[di]);
    const hourTargets = hourlyTargets?.[String(hour)]?.[dateStr] || {};
    const groupRows = GROUP_ORDER.map(({ key, name }) => {
      const recommended = Number(hourTargets[key] ?? 0);
      const scheduled = countGroupAtHour(di, hour, key);
      const { bg, text } = groupCellColor(scheduled, recommended, closed);
      return { key, name, scheduled, recommended, bg, text };
    });
    const anyUnderstaffedOpen = !closed && groupRows.some(r => r.scheduled < r.recommended);
    return { mode: "targets", header, groupRows, anyUnderstaffedOpen };
  })() : null;
  const tooltipLeft = (typeof window !== "undefined" && tooltipPos.x + 12 + 220 > window.innerWidth)
    ? tooltipPos.x - 232
    : tooltipPos.x + 12;

  if (isMobile) {
    const di = Math.max(0, Math.min(6, mobileDay));
    const o = openHour(di);
    const c = closeHour(di);
    const rows = (o === null || c === null)
      ? []
      : heatmapHours.filter(h => h >= o && h < c).sort((a, b) => a - b).map(hour => ({
          hour,
          ...mobileCoverage(hour, di),
        }));
    const visibleRows = showAllMobileHours ? rows : rows.slice(0, 6);
    return (
      <div style={{ marginTop: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 }}>
          {`Staff coverage — ${lang === "gr" ? dayNamesGr[di] : dayNames[di]}`}
        </div>
        {rows.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{lang === "gr" ? "Κλειστό" : "Closed"}</div>
        ) : (
          <>
            {visibleRows.map(r => {
              const dotColor = r.tone === "red" ? "#DC2626" : r.tone === "amber" ? "#F59E0B" : r.tone === "green" ? "#22C55E" : "#9CA3AF";
              const dateStr = isoDate(weekDates[di]);
              const hourTargets = hourlyTargets?.[String(r.hour)]?.[dateStr] || {};
              const groupBreakdown = [
                { key: "supervisor", label: "SUP" },
                { key: "service", label: "SVC" },
                { key: "kitchen", label: "KIT" },
                { key: "cleaning", label: "CLN" },
              ].map(g => {
                const scheduled = countGroupAtHour(di, r.hour, g.key);
                const recommended = Number(hourTargets[g.key] ?? 0);
                const hasTargetData = hourlyTargets != null;
                let color = "var(--text-muted)";
                if (hasTargetData) {
                  if (recommended === 0) color = "var(--text-muted)";
                  else if (scheduled === 0) color = "#DC2626";
                  else if (scheduled === recommended - 1) color = "#F59E0B";
                  else if (scheduled >= recommended) color = "#16A34A";
                }
                return { ...g, scheduled, color };
              });
              return (
                <div key={r.hour} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, width: 52, color: "var(--text-primary)" }}>{String(r.hour % 24).padStart(2, "0")}:00</span>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor }} />
                  <div style={{ display: "flex", gap: 10 }}>
                    {groupBreakdown.map(g => (
                      <div key={g.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 28 }}>
                        <span style={{ fontSize: 9, textTransform: "uppercase", color: "var(--text-muted)" }}>{g.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 900, color: g.color }}>{g.scheduled}</span>
                      </div>
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>{r.count} total</span>
                </div>
              );
            })}
            {rows.length > 6 && (
              <button
                onClick={() => setShowAllMobileHours(v => !v)}
                style={{ marginTop: 8, border: "none", background: "none", color: "var(--green)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "DM Sans,sans-serif", padding: 0 }}
              >
                {showAllMobileHours ? (lang === "gr" ? "Λιγότερες ώρες" : "Show fewer hours") : (lang === "gr" ? "Όλες οι ώρες" : "Show all hours")}
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 24, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 20, overflow: "hidden" }}>

      {/* Header */}
      <div
        onClick={onHeaderClick || undefined}
        style={{ padding: "16px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, cursor: onHeaderClick ? "pointer" : "default" }}
      >
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)" }}>
            {lang === "gr" ? "Κάλυψη Ωρών — Εβδομάδα" : "Weekly Staffing Coverage — ShiftIQ Targets"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            {hourlyTargets != null
              ? (lang === "gr"
                ? "Το ShiftIQ προτείνει ανά ομάδα · χρώμα = προγραμματισμένοι vs στόχος"
                : "ShiftIQ recommends per group · colour = scheduled vs target")
              : (lang === "gr"
                ? "Κάθε κελί = αριθμός εργαζομένων την ώρα αυτή · χρώμα = κάλυψη vs ζήτηση"
                : "Each cell = staff count for that hour · colour = coverage vs. demand")}
          </div>
        </div>
        {/* Coverage legend */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {[
            ["#D1FAE5", "#065F46", lang === "gr" ? "Εντός στόχου" : "Meets target"],
            ["#FEF3C7", "#92400E", lang === "gr" ? "1 κάτω από στόχο" : "1 below target"],
            ["#FEE2E2", "#DC2626", lang === "gr" ? "Κανείς (ανοιχτό)" : "Nobody (store open)"],
            ["#F9FAFB", "#E5E7EB", lang === "gr" ? "Κλειστό" : "Closed"],
          ].map(([bg, text, lbl]) => (
            <div key={lbl} style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div style={{ width: 12, height: 12, borderRadius: 2, background: bg, border: `1px solid ${text}` }} />
              <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:600 }}>{lbl}</span>
            </div>
          ))}
        </div>
        {headerRightAction}
      </div>
      <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 700, marginRight: 4 }}>{lang === "gr" ? "Υπόμνημα:" : "Legend:"}</span>
        {SPECIAL_STATUSES.map(sp => (
          <span key={sp.value} style={{ fontSize: 10, background: sp.color, color: sp.textColor, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>{lang === "gr" ? sp.labelGr : sp.label}</span>
        ))}
        <span style={{ fontSize: 10, background: "#ECFDF5", color: "#065F46", fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>09:00-17:00 = shift</span>
        <span style={{ fontSize: 10, background: "#FEE2E2", color: "#991B1B", fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>⚠️ = &lt;11h rest</span>
        <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>{lang === "gr" ? "Κλικ σε κελί για βάρδια" : "Click cell to assign shift"}</span>
      </div>

      {/* Grid */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
          <thead>
            <tr>
              {/* Hour col header */}
              <th style={{ width: 52, background: "var(--header-dark)", padding: "8px 10px", textAlign: "left" }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: 1 }}>
                  {lang === "gr" ? "ΩΡΑ" : "HOUR"}
                </span>
              </th>
              {weekDates.map((d, i) => (
                <th key={i} style={{ background: "var(--header-dark)", padding: "8px 6px", textAlign: "center", borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#fff" }}>{lang === "gr" ? dayNamesGr[i] : dayNames[i]}</div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontWeight: 500, marginTop: 1 }}>{fmtDate(d)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allHours.map(hour => {
              const pct  = INTENSITY[hour] ?? 0;
              const { dot } = demandLabel(pct);
              return (
                <tr key={hour}>
                  {/* Hour label */}
                  <td style={{ padding: "0 8px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "var(--surface2)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, height: 44 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-primary)" }}>
                          {String(hour % 24).padStart(2,"0")}:00
                        </span>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gridTemplateRows: "1fr 1fr",
                          gap: "1px",
                          marginTop: "3px",
                          width: "100%",
                        }}>
                          {["S","Sv","K","C"].map(l => (
                            <div key={l} style={{
                              fontSize: "8px",
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              textAlign: "center",
                              lineHeight: 1,
                            }}>{l}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Day cells */}
                  {weekDates.map((d, di) => {
                    const open = openHour(di);
                    const close = closeHour(di);
                    const closed = open === null || close === null || hour < open || hour >= close;
                    if (hourlyTargets == null) {
                      if (closed) return (
                        <td
                          key={di}
                          style={{ borderLeft: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: "#F9FAFB", position: "relative" }}
                          onMouseEnter={(e) => {
                            setHovered({ di, hour });
                            setTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => {
                            setTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <div style={{ height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ fontSize: 10, color: "#E5E7EB" }}>—</span>
                          </div>
                        </td>
                      );
                      const count = countAtHour(di, hour);
                      const { bg, text } = cellColor(count, pct);
                      return (
                        <td
                          key={di}
                          style={{ borderLeft: "1px solid var(--border)", borderBottom: "1px solid var(--border)", padding: 0, position: "relative" }}
                          onMouseEnter={(e) => {
                            setHovered({ di, hour });
                            setTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => {
                            setTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <div style={{ height: 36, display: "flex", alignItems: "center", justifyContent: "center", background: bg, transition: "background 0.15s" }}>
                            <span style={{ fontSize: 13, fontWeight: 900, color: text }}>
                              {count === 0 && pct >= 40 ? "⚠" : count}
                            </span>
                          </div>
                        </td>
                      );
                    }

                    const dateStr = isoDate(d);
                    const hourTargets = hourlyTargets?.[String(hour)]?.[dateStr] || {};
                    const groupRows = GROUP_ORDER.map(({ key, name }) => {
                      const recommended = Number(hourTargets[key] ?? 0);
                      const scheduled = countGroupAtHour(di, hour, key);
                      const { bg, text } = groupCellColor(scheduled, recommended, closed);
                      return { key, name, recommended, scheduled, bg, text };
                    });
                    return (
                      <td
                        key={di}
                        style={{ borderLeft: "1px solid var(--border)", borderBottom: "1px solid var(--border)", padding: 2, position: "relative" }}
                        onMouseEnter={(e) => {
                          setHovered({ di, hour });
                          setTooltipPos({ x: e.clientX, y: e.clientY });
                        }}
                        onMouseMove={(e) => {
                          setTooltipPos({ x: e.clientX, y: e.clientY });
                        }}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gridTemplateRows: "1fr 1fr",
                          gap: "1px",
                          height: "100%",
                          width: "100%",
                        }}>
                          {groupRows.map(({ key, recommended, bg, text }) => {
                            return (
                              <div
                                key={key}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "11px",
                                  fontWeight: 900,
                                  background: bg,
                                  color: text,
                                  minWidth: 0,
                                  minHeight: 0,
                                }}
                              >
                                <span style={{ fontSize: 11, fontWeight: 900 }}>{recommended}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Coverage key */}
      <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>{lang === "gr" ? "Κάλυψη:" : "Coverage:"}</span>
        {[
          ["#D1FAE5","#065F46", lang === "gr" ? "Εντός στόχου" : "Meets target"],
          ["#FEF3C7","#92400E", lang === "gr" ? "1 κάτω από στόχο" : "1 below target"],
          ["#FEE2E2","#DC2626", lang === "gr" ? "Κανείς (ανοιχτό)" : "Nobody (store open)"],
          ["#F9FAFB","#E5E7EB", lang === "gr" ? "Κλειστό" : "Closed"],
        ].map(([bg,text,lbl]) => (
          <div key={lbl} style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ width:18, height:14, background:bg, border:`1.5px solid ${text}30`, borderRadius:3 }} />
            <span style={{ fontSize:10, color:"var(--text-muted)" }}>{lbl}</span>
          </div>
        ))}
      </div>
      {hoveredInfo && (
        <div style={{
          position: "fixed",
          left: tooltipLeft,
          top: tooltipPos.y - 10,
          transform: "translateY(-100%)",
          zIndex: 1000,
          pointerEvents: "none",
          background: COVERAGE_HEATMAP_TOOLTIP_BG,
          color: "#fff",
          borderRadius: 12,
          padding: "12px 14px",
          fontSize: 12,
          lineHeight: 1.5,
          minWidth: 220,
          maxWidth: 260,
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          whiteSpace: "normal",
        }}>
          <div style={{ fontWeight: 800, fontSize: 11, color: "#F59E0B", marginBottom: 8 }}>
            {hoveredInfo.header}
          </div>
          {hoveredInfo.mode === "fallback" ? (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
              {`Staff at this hour: ${hoveredInfo.count}`}
            </div>
          ) : (
            <>
              {hoveredInfo.groupRows.map(({ key, name, scheduled, recommended, bg }) => (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, fontSize: 11 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: tooltipDotColor(bg), flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, minWidth: 72 }}>{name}</span>
                  <span style={{ color: "rgba(255,255,255,0.7)" }}>{`scheduled ${scheduled} / target ${recommended}`}</span>
                </div>
              ))}
              <div style={{ marginTop: 8, fontSize: 10, color: "rgba(255,255,255,0.55)", fontStyle: "italic" }}>
                {hoveredInfo.anyUnderstaffedOpen ? "Understaffed — assign shifts above to resolve" : "Fully staffed ✓"}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


